/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type { RoomData } from '../../sync/interface';

export enum RealtimeTransactionType {
  Chat = 'chat',
  Post = 'post_notification',
  Comment = 'comment_notification',
}

export type RawNotiMessage = {
  transaction_type: RealtimeTransactionType;
  message: string;
  detail: any;
  new_message_count?: number;
  [key: string]: any;
};

// ---- Public Event Types ----

export enum RealtimeEvent {
  Connected = 'realtime.connected',
  Disconnected = 'realtime.disconnected',
  Reconnecting = 'realtime.reconnecting',
  Error = 'realtime.error',
  NewMessage = 'realtime.message.new',
  RoomUpdated = 'realtime.room.updated',
  Presence = 'realtime.presence',
  ChannelSubscribed = 'realtime.channel.subscribed',
  ChannelUnsubscribed = 'realtime.channel.unsubscribed',
  NewNotification = 'realtime.notification.new',
  TokenRefreshed = 'realtime.token.refreshed',
}

// Basic message & room interfaces - can be extended later
export type RealtimeMessagePayload = {
  transaction_type: RealtimeTransactionType.Chat;
  message: string;
  detail: any;
  new_message_count?: number;
  key: string; // client-generated unique key for deduplication

  [key: string]: any;
};

export type RealtimeRoomUpdatePayload = {
  transaction_type: RealtimeTransactionType.Chat;
  message: string;
  detail: RoomData;
  new_message_count?: number;
  post_id?: string;
  post_title?: string;
  post_type?: string;
};

export type ConnectionInfo = {
  session: string;
  transport: string;
};

export type RealtimeError = {
  code?: number;
  message: string;
  cause?: unknown;
};

export type RealtimeEventHandlerMap = {
  [RealtimeEvent.Connected]: (info: ConnectionInfo) => void;
  [RealtimeEvent.Disconnected]: (reason: string) => void;
  [RealtimeEvent.Reconnecting]: (attempt: number) => void;
  [RealtimeEvent.Error]: (err: RealtimeError) => void;
  [RealtimeEvent.NewMessage]: (msg: RealtimeMessagePayload) => void;
  [RealtimeEvent.RoomUpdated]: (update: RealtimeRoomUpdatePayload) => void;
  [RealtimeEvent.Presence]: (presence: Record<string, boolean>) => void;
  [RealtimeEvent.ChannelSubscribed]: (channel: string) => void;
  [RealtimeEvent.ChannelUnsubscribed]: (channel: string) => void;
  [RealtimeEvent.NewNotification]: (noti: RawNotiMessage) => void;
  [RealtimeEvent.TokenRefreshed]: () => void;
};

// Structural Centrifuge-like client interface (subset) for dependency inversion.
export type ICentrifugeLike = {
  connect: () => void;
  disconnect: () => void;
  on: (event: string, cb: (...args: any[]) => void) => void;
  removeListener: (event: string, cb: (...args: any[]) => void) => void;
  subscribe: (channel: string, cb: (ctx: { data: any }) => void) => { unsubscribe: () => void };
  setToken?: (token: string) => void;
};

export type RealtimeOptions = {
  url: string;
  /** List of default channels to subscribe immediately after connection. */
  channels: string[];
  /** Optional factory returning a centrifuge-like instance. If omitted a Centrifuge will be constructed with `url`. */
  centrifugeFactory?: () => ICentrifugeLike;
  /** Websocket endpoint used by default factory (required if no centrifugeFactory). */
  /** Optional async token refresher. Return new token string. */
  tokenProvider?: () => Promise<string>;
  /** Enable automatic token refresh on authentication errors. Default: true */
  autoRefreshToken?: boolean;
  /** Transform raw message channel payload -> typed message; return undefined to skip emit. */
  mapMessage?: (raw: any) => RealtimeMessagePayload | undefined;
  /** Transform raw room update payload */
  mapRoomUpdate?: (raw: any) => RealtimeRoomUpdatePayload | undefined;
  /** Transform raw presence payload */
  mapPresence?: (raw: Record<string, boolean>) => Record<string, boolean> | undefined;
  /** Logger (console-like) */
  logger?: { debug: (...a: any[]) => void; warn: (...a: any[]) => void; error: (...a: any[]) => void };
  /** Auto reconnect attempts (simple incremental) */
  maxReconnectAttempts?: number;
  /** Delay base (ms) for reconnect backoff (attempt * base) */
  reconnectBaseDelayMs?: number;
  /**
   * Optional token to use for initial connection, if not provided we will use accessToken from client.
   */
  token?: string;
};
